import { useNotify } from '@globaleris/ui.library'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { EnumTipoCicloFacturacion, EnumTipoFacturacion, EnumTipoSujeto } from '../../../api'
import { EnumEstadoCiclo } from '../../../api/oData/enums/enumEstadoCiclo'
import { useConfirmationDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import {
  fetchInvoiceCycleExecution,
  updateInvoiceCycleExecution
} from '../../invoiceCycles/invoiceCycles.service'
import { RegenerateFormData } from '../settlements.models'
import { closeExecutionCycleAsync, generateLiquidationsAsync } from '../settlements.service'

interface UseExecutionCycleActionsParams {
  executionCycleId: number
  invoiceCycleId: number
  invoiceCycleDescription?: string
  invoiceCycleType?: EnumTipoCicloFacturacion
  subjectType?: EnumTipoSujeto
  billingType?: EnumTipoFacturacion
  /** Estado actual del ciclo — se pasa como originState al navegar a processing para que el polling sepa de dónde viene */
  currentCycleState?: EnumEstadoCiclo
  executionInitialData?: RegenerateFormData
  /**
   * Se invoca al terminar el Regenerar (con o sin éxito). Si la pantalla lo informa, con éxito se queda en
   * ella en lugar de navegar a Procesando (la pantalla muestra el faldón a la espera de que arranque).
   */
  onRegenerateResult?: (isSuccess: boolean) => void
  /**
   * Se invoca cuando Regenerar o Cerrar se bloquean porque la ejecución está Procesando o Cerrada (cambió
   * desde otra ventana): la pantalla recarga la ejecución para mostrar su estado real.
   */
  onExecutionNotModifiable?: () => void
}

/**
 * Custom hook que proporciona handlers compartidos para acciones del ciclo de ejecución.
 * Se utiliza en las pantallas de Settlements y SettlementsInvoices para mantener
 * la lógica consistente entre ambas.
 *
 * @param params - Parámetros necesarios para las acciones del ciclo
 * @param params.executionCycleId - ID del ciclo de ejecución
 * @param params.invoiceCycleId - ID del ciclo de facturación
 * @param params.invoiceCycleDescription - Descripción del ciclo (opcional)
 * @param params.invoiceCycleType - Tipo de ciclo de facturación -- requerido para redirección solamente
 * @param params.subjectType - Tipo de sujeto -- requerido para redirección solamente
 * @param params.billingType - Tipo de facturación -- requerido para redirección solamente
 *
 * @returns Objeto con los handlers:
 * - handleRegenerateExecutionCycle: Regenera el ciclo de ejecución (desde Validar facturas abre antes el
 *   diálogo de elección Regenerar / Navegar sin regenerar)
 * - closeExecutionCycle: Cierra el ciclo de ejecución
 * - ensureExecutionCanBeModified: Comprueba que la ejecución no esté Procesando ni Cerrada antes de
 *   Regenerar o Cerrar (acciones sobre la ejecución, no sobre un lote)
 */
export const useExecutionCycleActions = ({
  executionCycleId,
  invoiceCycleId,
  invoiceCycleDescription,
  invoiceCycleType,
  subjectType,
  billingType,
  currentCycleState,
  executionInitialData,
  onRegenerateResult,
  onExecutionNotModifiable
}: UseExecutionCycleActionsParams) => {
  const [t] = useTranslation(['settlementsInvoices'])
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { info, error } = useNotify()
  const history = useHistory()
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false)
  const [regenerateChoiceDialogOpen, setRegenerateChoiceDialogOpen] = useState(false)

  /**
   * Regenerar y Cerrar actúan sobre la ejecución completa y no deben lanzarse con la ejecución Cerrada.
   * Se lee el estado actual (la pantalla puede estar desactualizada).
   * La comprobación es informativa (el back decide): si la lectura falla no se bloquea la acción.
   *
   * @param blockWhenProcessing - Además bloquea con la ejecución Procesando (Cerrar). Regenerar la
   * permite: el back gestiona el cambio de estado.
   */
  const checkExecutionStatus = async (blockWhenProcessing: boolean): Promise<boolean> => {
    const response = await fetchInvoiceCycleExecution(executionCycleId)
    if (!response.success || !response.data) {
      return true
    }

    if (blockWhenProcessing && response.data.status === EnumEstadoCiclo.Procesando) {
      error(t('settlementsInvoices:notifications.executionNotModifiable.processing'))
      onExecutionNotModifiable?.()
      return false
    }

    if (response.data.status === EnumEstadoCiclo.Finalizado) {
      error(t('settlementsInvoices:notifications.executionNotModifiable.closed'))
      onExecutionNotModifiable?.()
      return false
    }

    return true
  }

  /** Cerrar: no se permite con la ejecución Procesando ni Cerrada */
  const ensureExecutionCanBeModified = () => checkExecutionStatus(true)

  /** Regenerar tras la confirmación: anuladoras/refacturación vuelven a Validar listado, el resto abre el formulario */
  const startRegenerate = async () => {
    const isAnnulationOrRefacturation =
      billingType === EnumTipoFacturacion.Anuladora ||
      billingType === EnumTipoFacturacion.Refacturacion

    if (isAnnulationOrRefacturation) {
      const executionResponse = await fetchInvoiceCycleExecution(executionCycleId)

      const patchResponse = await updateInvoiceCycleExecution(executionCycleId, {
        cycleStatus: EnumEstadoCiclo.PdteValidarListado
      })

      if (!patchResponse.success) {
        error(t('settlementsInvoices:fetchResponse.updateExecutionCycleState.error'))
        closeConfirmation()
        return
      }

      closeConfirmation()
      history.push(NonAuthRoutes.SettlementsAnnulation, {
        executionCycleId,
        invoiceCycleId,
        billingType,
        startDate: executionResponse.data?.startDate,
        endDate: executionResponse.data?.endDate
      })
    } else {
      closeConfirmation()
      setRegenerateDialogOpen(true)
    }
  }

  const handleRegenerateExecutionCycle = async () => {
    if (!(await checkExecutionStatus(false))) return

    // Desde Validar facturas se ofrece regenerar o volver a Validar listado sin regenerar
    if (currentCycleState === EnumEstadoCiclo.PdteValidarFacturas) {
      setRegenerateChoiceDialogOpen(true)
      return
    }

    await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_REGENERATE_EXECUTION_CYCLE).then(
      startRegenerate
    )
  }

  const handleRegenerateChoiceDialogClose = () => setRegenerateChoiceDialogOpen(false)

  const handleRegenerateChoiceConfirm = async () => {
    setRegenerateChoiceDialogOpen(false)
    await startRegenerate()
  }

  const handleNavigateWithoutRegenerate = async () => {
    setRegenerateChoiceDialogOpen(false)

    // Anuladoras/refacturación tienen su propio Validar listado (settlements.annulation)
    const isAnnulationOrRefacturation =
      billingType === EnumTipoFacturacion.Anuladora ||
      billingType === EnumTipoFacturacion.Refacturacion

    if (isAnnulationOrRefacturation) {
      const executionResponse = await fetchInvoiceCycleExecution(executionCycleId)
      history.push(NonAuthRoutes.SettlementsAnnulation, {
        executionCycleId,
        invoiceCycleId,
        billingType,
        startDate: executionResponse.data?.startDate,
        endDate: executionResponse.data?.endDate
      })
      return
    }

    history.push(NonAuthRoutes.Settlements, { executionCycleId, invoiceCycleId })
  }

  const handleRegenerateDialogClose = async (formData?: RegenerateFormData) => {
    setRegenerateDialogOpen(false)
    if (!formData) return
    const response = await generateLiquidationsAsync(executionCycleId, formData)
    if (response.success) {
      info(t('settlementsInvoices:fetchResponse.generateLiquidations.inProgress'))
    } else {
      error(
        response.error?.message ??
          t('settlementsInvoices:fetchResponse.generateLiquidations.error')
      )
      // El back puede haber actualizado fechas/subtipo antes de fallar: la pantalla recarga la ejecución
      onRegenerateResult?.(false)
      return
    }

    // Desde Validar facturas la regeneración vuelve a Validar listado, esperando que la ejecución arranque
    if (currentCycleState === EnumEstadoCiclo.PdteValidarFacturas) {
      history.push(NonAuthRoutes.Settlements, {
        executionCycleId,
        invoiceCycleId,
        // Clave propia (no originState): Procesando y el mini-layer reenvían originState al navegar
        waitForProcessingFrom: currentCycleState
      })
      return
    }

    if (onRegenerateResult) {
      onRegenerateResult(true)
      return
    }

    history.push(NonAuthRoutes.invoiceCycleProcessing, {
      executionCycleId,
      invoiceCycleId,
      cycleName: invoiceCycleDescription,
      originState: currentCycleState
    })
  }

  /**
   * Cierra el ciclo de ejecución llamando al servicio correspondiente.
   * Las fechas deben ser proporcionadas desde un formulario aparte.
   *
   * @param generateInvoices - true si debe generar facturas (deleteLiquidations=false), false si no (deleteLiquidations=true)
   * @param startDate - Fecha de inicio del ciclo de ejecución (ISO string)
   * @param endDate - Fecha de fin del ciclo de ejecución (ISO string)
   * @param reInvoiceType - Tipo de re-facturación (opcional, solo para ciertos tipos de ciclo)
   * @param groupingType - Tipo de agrupación para la generación de facturas (opcional, solo para ciertos tipos de ciclo)
   */
  const closeExecutionCycle = async (
    generateInvoices: boolean,
    startDate?: string,
    endDate?: string,
    reInvoiceType?: number,
    groupingType?: number
  ) => {
    try {
      const response = await closeExecutionCycleAsync({
        executionId: executionCycleId,
        deleteLiquidations: !generateInvoices, // invertido: si genera facturas, NO elimina liquidaciones
        cfExecutionStartDate: startDate,
        cfExecutionEndDate: endDate,
        reInvoiceType,
        groupingType
      })

      if (response.success) {
        closeConfirmation()
        info(t('settlementsInvoices:fetchResponse.closeExecutionCycle.success'))
        history.push(NonAuthRoutes.invoiceCycleFinished, {
          executionCycleId,
          invoiceCycleId,
          invoiceCycleType,
          subjectType,
          billingType
        })
      } else {
        error(
          response.error?.message ?? t('settlementsInvoices:fetchResponse.closeExecutionCycle.error')
        )
      }
    } catch (err) {
      error(t('settlementsInvoices:fetchResponse.closeExecutionCycle.error'))
    }
  }

  return {
    handleRegenerateExecutionCycle,
    closeExecutionCycle,
    ensureExecutionCanBeModified,
    regenerateChoiceDialogOpen,
    handleRegenerateChoiceDialogClose,
    handleRegenerateChoiceConfirm,
    handleNavigateWithoutRegenerate,
    regenerateDialogOpen,
    handleRegenerateDialogClose,
    regenerateInitialData: executionInitialData
  }
}
